import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { friendlyError } from '../utils/friendly-error';

export type AvailabilityBlockReason = 'holiday' | 'maintenance' | 'emergency' | 'private_event' | 'other';

/** Fechas (o franja horaria) en que un espacio o el negocio completo no recibe reservas. */
export interface AvailabilityBlock {
  block_id: number;
  business_id: number;
  /** null = todo el negocio. */
  space_id: number | null;
  space_title: string | null;
  scope: 'space' | 'business';
  start_date: string;
  end_date: string;
  all_day: boolean;
  start_time: string | null;
  end_time: string | null;
  reason: AvailabilityBlockReason;
  reason_label: string;
  /** Interna: solo la ve el equipo del negocio. */
  note: string | null;
}

export interface AvailabilityBlockPayload {
  space_id: number | null;
  start_date: string;
  end_date: string;
  all_day: boolean;
  start_time?: string | null;
  end_time?: string | null;
  reason: AvailabilityBlockReason;
  note?: string | null;
}

/** Reserva activa que cae en un bloqueo (no se cancela sola: se avisa al negocio). */
export interface AffectedReservation {
  reservation_id: number;
  reference: number;
  space_title: string | null;
  date: string;
  start_time: string | null;
  end_time: string | null;
  customer_name: string | null;
  status: string;
}

export interface SaveBlockResponse {
  msg: string;
  block: AvailabilityBlock;
  affected_reservations: AffectedReservation[];
}

export const AVAILABILITY_REASONS: Array<{ value: AvailabilityBlockReason; label: string; icon: string }> = [
  { value: 'holiday', label: 'Día de asueto', icon: 'bi-calendar-heart' },
  { value: 'maintenance', label: 'Mantenimiento', icon: 'bi-tools' },
  { value: 'emergency', label: 'Emergencia', icon: 'bi-exclamation-triangle' },
  { value: 'private_event', label: 'Evento privado', icon: 'bi-lock' },
  { value: 'other', label: 'Otro motivo', icon: 'bi-slash-circle' },
];

@Injectable({ providedIn: 'root' })
export class AvailabilityBlockService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/availability-blocks`;

  private fail = (fallback: string) => (error: HttpErrorResponse): Observable<never> =>
    throwError(() => new Error(friendlyError(error, fallback)));

  /** Con spaceId: los del espacio y los del negocio completo (lo que le aplica). */
  list(businessId: number, spaceId?: number | null): Observable<{ blocks: AvailabilityBlock[]; today: string }> {
    const query = spaceId ? `?space_id=${spaceId}` : '';
    return this.http.get<{ blocks: AvailabilityBlock[]; today: string }>(`${this.url}/business/${businessId}${query}`)
      .pipe(catchError(this.fail('No se pudieron cargar los días no disponibles.')));
  }

  create(businessId: number, payload: AvailabilityBlockPayload): Observable<SaveBlockResponse> {
    return this.http.post<SaveBlockResponse>(`${this.url}/business/${businessId}`, payload)
      .pipe(catchError(this.fail('No se pudo guardar el bloqueo.')));
  }

  update(blockId: number, payload: AvailabilityBlockPayload): Observable<SaveBlockResponse> {
    return this.http.put<SaveBlockResponse>(`${this.url}/${blockId}`, payload)
      .pipe(catchError(this.fail('No se pudo actualizar el bloqueo.')));
  }

  remove(blockId: number): Observable<{ msg: string }> {
    return this.http.delete<{ msg: string }>(`${this.url}/${blockId}`)
      .pipe(catchError(this.fail('No se pudo quitar el bloqueo.')));
  }
}
